import { Stack } from '@rabex-kit/core';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import React, { memo, useEffect, useState } from 'react';
import {
  Tab,
  TabContext,
  TabPanel,
  Tabs,
  Typography,
  useMediaQuery,
  useResponsive,
  useTheme
} from '@rabex-kit/rabex-ui';
import LoginForm, { emailRegex } from '../../components/Login';
import { useLoginMutation } from '../../services/auth';
import { LoginRequestBody } from '../../services/auth/types';
import MockUp from '../../static/images/svg/auth/LoginWithoutQr.svg';
import DarkMockUp from '../../static/images/svg/auth/Login-dark.svg';
import TokenManager from '../../utils/tokenManager';
import * as routes from '../../pages/routes';
import parseJwt from '../../utils/jwtParser';
import LoginWithQrCode from './Qr';
import RegisterContainer from '../Register';
import MockUpComponent from '../Register/mockUp';
import snackbarInstances from '../../utils/snackbar';
import { afterLoginPath, returnPathFrom } from '@/utils/loginReturn';
import { OTP_DESCRIPTION, OTP_TITLE, OTPType } from '@/constants/authOtp';

const LoginContainer = () => {
  const { matches } = useResponsive();
  const theme = useTheme();
  const { palette } = theme;
  const navigate = useNavigate();
  const [loginRequest, { isLoading, isSuccess }] = useLoginMutation();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const location = useLocation();
  // The page that sent the guest here, carried through OTP so they land back on it.
  const returnTo = returnPathFrom(location.state);
  const { loginTab: urlTab, referralCode } = useParams<{
    loginTab?: string;
    referralCode?: string;
  }>();
  const activeTab: 'login' | 'register' =
    urlTab === 'register' ? 'register' : 'login';

  const [loginTab, setLoginTab] = useState<'login' | 'register'>(activeTab);

  // src/containers/Login/index.tsx
  // Nothing here is logged: the login response carries the access token.
  const handleOnSubmit = async (values: LoginRequestBody) => {
    try {
      // The form shows the server's message under the password field, so no toast as well.
      const res = await loginRequest({
        ...values,
        preventSnackbar: true
      }).unwrap();

      const { accessToken } = res.data;

      if (!accessToken) {
        throw new Error('No access token received');
      }

      // Only a pre-OTP token: held in memory until the code is verified (containers/Otp).
      TokenManager.setPending(accessToken);

      // Parse for MFA
      const userData = parseJwt(accessToken);
      const otpType: OTPType = userData.MFA?.[0] || 'sms';

      // SMS goes to the account's mobile, which is not what was typed when logging in by email.
      const smsNumber = emailRegex.test(values.phoneNumber)
        ? userData?.Mobile || ''
        : values.phoneNumber;
      const highlight = {
        email: userData?.Email || values.phoneNumber,
        sms:
          smsNumber.replace(/(\d{4})(\d{3})(\d{2})(\d{2})/g, '$1 $2 $3 $4') ||
          'همراه شما',
        ga: 'Google Authenticator'
      };

      // Navigate to OTP
      navigate(routes.OTP, {
        state: {
          title: OTP_TITLE[otpType],
          description: OTP_DESCRIPTION[otpType],
          highlight: highlight[otpType],
          from: 'login',
          type: otpType,
          returnTo
        }
      });
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('[Login] Login error:', error);
      }
      throw error;
    }
  };

  const handleChangeTab = (
    event: React.SyntheticEvent,
    value: 'login' | 'register'
  ) => {
    setLoginTab(value);

    // Navigate to the appropriate URL
    // Keep the router state (a return page) and an invite's referral code across tab switches.
    const base = value === 'register' ? routes.REGISTER : routes.LOGIN;
    navigate(referralCode ? `${base}/${referralCode}` : base, {
      replace: true,
      state: location.state
    });
  };

  // Someone already signed in has nothing to do here: on to the return page or the dashboard.
  // A pending token (mid-OTP) is not a session, so it doesn't count.
  useEffect(() => {
    TokenManager.hasToken().then((signedIn) => {
      if (signedIn) navigate(afterLoginPath(returnTo), { replace: true });
    });
  }, []);

  // Sync local state with URL parameter
  useEffect(() => {
    setLoginTab(activeTab);
  }, [activeTab]);

  useEffect(() => {
    const message = sessionStorage.getItem('flashMessage');
    if (message) {
      snackbarInstances.success(message);
      // enqueueSnackbar(message, { variant: 'success' });
      sessionStorage.removeItem('flashMessage');
    }
  }, []);

  return (
    <>
      <Stack
        gap={matches({ xs: 3, lg: 4 })}
        // Above the desktop mockup's animated layers; on mobile there is no mockup, and 1200 put
        // the form over the header's drawer.
        sx={{ zIndex: { xs: 'auto', lg: 1200 } }}
        width={{ xs: '100%', lg: 'unset' }}>
        <Stack py={3} px={4} bgcolor={palette.base[100]} borderRadius={1.5}>
          <Typography
            variant={matches({ xs: 'body3', lg: 'body3' })}
            color={palette.textColor[900]}>
            توجه کنید آدرس مرورگر دقیقا &nbsp;
            <Typography
              variant="body3"
              weight="semiBold"
              component="span"
              color={palette.primary[500]}>
              https://p.rabex.ir
            </Typography>
            &nbsp;باشد.
          </Typography>
        </Stack>
        <TabContext value={loginTab}>
          <Stack gap={matches({ xs: 5, lg: 8 })}>
            <Stack
              flexDirection="row"
              sx={{ borderBottom: `1px solid ${theme.palette.base[300]}` }}>
              <Tabs
                value={loginTab}
                size="lg"
                mode="underline"
                variant={matches({ xs: 'scrollable', lg: 'standard' })}
                onChange={handleChangeTab}>
                <Tab
                  label={
                    <Typography
                      variant="body2"
                      weight="semiBold"
                      color={
                        loginTab === 'login'
                          ? theme.palette.primary[500]
                          : theme.palette.textColor[900]
                      }>
                      ورود
                    </Typography>
                  }
                  value="login"
                />
                <Tab
                  label={
                    <Typography
                      variant="body2"
                      weight="semiBold"
                      color={
                        loginTab === 'register'
                          ? palette.primary[500]
                          : palette.textColor[900]
                      }>
                      ثبت نام
                    </Typography>
                  }
                  value="register"
                />
              </Tabs>
            </Stack>
            <TabPanel value="login">
              <LoginForm
                onSubmit={handleOnSubmit}
                isLoading={isLoading || isSuccess}
              />
            </TabPanel>
            <TabPanel value="register">
              <RegisterContainer />
            </TabPanel>
          </Stack>
        </TabContext>
      </Stack>
      {!isMobile &&
        (loginTab === 'login' ? (
          <Stack
            sx={{
              zIndex: 1,
              maxWidth: 656,
              position: 'relative',
              width: 656,
              height: 613,
              overflow: 'visible',
              top: '-120px',
              right: '-60px'
            }}>
            <img
              src={theme.palette.mode === 'light' ? MockUp : DarkMockUp}
              alt="mockup"
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -56%)'
              }}
            />
            <Stack
              sx={{
                zIndex: 1,
                width: '357px',
                height: '451px',
                margin: '14px 0 0 162px',
                borderRadius: '24px',
                overflow: 'hidden',
                position: 'relative',
                '&:after': {
                  content: "''",
                  position: 'absolute',
                  transform: 'translateX(100%)',
                  inset: '0px',
                  '@media (prefers-reduced-motion: no-preference)': {
                    animation:
                      '2s linear 0.5s infinite normal none running login_motion'
                  },
                  // background:
                  //   'linear-gradient( 90deg, rgb(243 243 243 / 30%), transparent)'
                  background:
                    'linear-gradient( -60deg, transparent 25%, rgb(255 255 255 / 50%), transparent 75%)!important'
                }
              }}
            />
            <Stack
              sx={{
                opacity: 0.6,
                top: '170px',
                left: '-200px',
                height: '571px',
                width: '571px',
                position: 'absolute',
                zIndex: -1,
                '@media (prefers-reduced-motion: no-preference)': {
                  animation: 'fadeInOut 12s linear infinite'
                },
                background:
                  'radial-gradient(285px, rgba(255, 201, 0, 0.25) 0%, rgba(0, 0, 0, 0) 95%)'
              }}
            />
            <Stack
              sx={{
                opacity: 0.6,
                top: '200px',
                left: '130px',
                height: '591px',
                width: '591px',
                position: 'absolute',
                zIndex: -1,
                '@media (prefers-reduced-motion: no-preference)': {
                  animation: 'fadeInOut 14s linear infinite'
                },
                background:
                  'radial-gradient(295px, rgba(0, 255, 137, 0.25) 0%, rgba(0, 0, 0, 0) 95%)'
              }}
            />
            <Stack
              sx={{
                opacity: 0.6,
                top: '100px',
                left: '0px',
                height: '611px',
                width: '611px',
                position: 'absolute',
                zIndex: -1,
                '@media (prefers-reduced-motion: no-preference)': {
                  animation: 'fadeInOut 10s linear infinite'
                },
                background:
                  'radial-gradient(305px, rgba(56, 97, 251, 0.22) 0%, rgba(0, 0, 0, 0) 95%)'
              }}
            />
            <LoginWithQrCode />
          </Stack>
        ) : (
          <MockUpComponent />
        ))}
    </>
  );
};

export default memo(LoginContainer);
